import { UseSearchResult } from "@saleor/hooks/makeSearch";
import { findValueInEnum, maybe } from "@saleor/misc";
import {
  SearchBrands,
  SearchBrandsVariables
} from "@saleor/searches/types/SearchBrands";
import {
  SearchMalls,
  SearchMallsVariables
} from "@saleor/searches/types/SearchMalls";
import { PaymentChannelCodeEnum } from "@saleor/types/globalTypes";
import moment from "moment";

import { IFilterElement } from "../../../../components/Filter";
import {
  createFilterTabUtils,
  createFilterUtils,
  dedupeFilter,
  getGteLteVariables,
  getMinMaxQueryParam,
  getMultipleValueQueryParam,
  getSingleEnumValueQueryParam
} from "../../../../utils/filters";
import { removeBase64Name } from "../../../utils/misc";
import {
  OrderLinesFilterKeys,
  OrderLinesFinanceListFilterOpts,
  paymentEnum
} from "../../components/OrderLinesFinanceListPage/filters";
import {
  InitialOrderLinesFinanceFilterData_brands_edges_node,
  InitialOrderLinesFinanceFilterData_malls_edges_node
} from "../../types/InitialOrderLinesFinanceFilterData";
import {
  OrderLinesFinanceListUrlFilters,
  OrderLinesFinanceListUrlFiltersEnum,
  OrderLinesFinanceListUrlFiltersWithMultipleValues,
  OrderLinesUrlQueryParams
} from "../../urls";
export const ORDER_LINE_FINANCE_FILTERS_KEY = "OrderLinesFinanceFilters";

export function getFilterOpts(
  params: OrderLinesFinanceListUrlFilters,
  brands: {
    initial: InitialOrderLinesFinanceFilterData_brands_edges_node[];
    search: UseSearchResult<SearchBrands, SearchBrandsVariables>;
  },
  malls: {
    initial: InitialOrderLinesFinanceFilterData_malls_edges_node[];
    search: UseSearchResult<SearchMalls, SearchMallsVariables>;
  }
): OrderLinesFinanceListFilterOpts {
  const paymentMethod = params.paymentMethodType?.split(" ");
  const paymentMethodType = paymentMethod ? paymentMethod[0] : null;
  const channelCode = paymentMethod ? paymentMethod[1] : null;

  return {
    brand: {
      active: !!params.brand,
      choices: maybe(
        () =>
          brands.search.result.data.search.edges.map(edge => ({
            label: edge.node.name,
            value: edge.node.id
          })),
        []
      ),
      displayValues: !!params.brand
        ? maybe(
            () =>
              brands.initial.map(brand => ({
                label: brand.name,
                value: brand.id
              })),
            []
          )
        : [],
      hasMore: maybe(
        () => brands.search.result.data.search.pageInfo.hasNextPage,
        false
      ),
      initialSearch: "",
      loading: brands.search.result.loading,
      onFetchMore: brands.search.loadMore,
      onSearchChange: brands.search.search,
      value: maybe(() => dedupeFilter(params.brand), [])
    },
    canceledAt: {
      active: maybe(
        () =>
          [params.orderCanceledFrom, params.orderCanceledTo].some(
            field => field !== undefined
          ),
        false
      ),
      value: {
        max: maybe(() => params.orderCanceledTo, ""),
        min: maybe(() => params.orderCanceledFrom, "")
      }
    },
    channelCode: {
      active: !!channelCode,
      value: maybe(() =>
        channelCode
          ? findValueInEnum(channelCode, PaymentChannelCodeEnum)
          : null
      )
    },
    completedAt: {
      active: maybe(
        () =>
          [params.orderCompleteDateFrom, params.orderCompleteDateTo].some(
            field => field !== undefined
          ),
        false
      ),
      value: {
        max: maybe(() => params.orderCompleteDateTo, ""),
        min: maybe(() => params.orderCompleteDateFrom, "")
      }
    },
    mall: {
      active: !!params.mall,
      choices: maybe(
        () =>
          malls.search.result.data.search.edges.map(edge => ({
            label: edge.node.name,
            value: edge.node.id
          })),
        []
      ),
      displayValues: !!params.mall
        ? maybe(
            () =>
              malls.initial.map(mall => ({
                label: mall.name,
                value: mall.id
              })),
            []
          )
        : [],
      hasMore: maybe(
        () => malls.search.result.data.search.pageInfo.hasNextPage,
        false
      ),
      initialSearch: "",
      loading: malls.search.result.loading,
      onFetchMore: malls.search.loadMore,
      onSearchChange: malls.search.search,
      value: maybe(() => dedupeFilter(params.mall), [])
    },
    orderDate: {
      active: maybe(
        () =>
          [params.orderDateFrom, params.orderDateTo].some(
            field => field !== undefined
          ),
        false
      ),
      value: {
        max: maybe(() => params.orderDateTo, ""),
        min: maybe(() => params.orderDateFrom, "")
      }
    },
    orderStatus: {
      active: !!params.orderStatus,
      value: maybe(() => dedupeFilter(params.orderStatus), [])
    },
    paymentMethodType: {
      active: !!paymentMethodType,
      value: maybe(() => paymentMethodType, "")
    },
    shippingMethodName: {
      active: !!params.deliveryType,
      value: maybe(() => dedupeFilter(params.deliveryType), [])
    }
  };
}

export function getFilterVariables(params: OrderLinesFinanceListUrlFilters) {
  const newDate = new Date();
  const endDay = newDate.getDate();
  const endMonth = newDate.getMonth() + 1;
  const endYear = newDate.getFullYear();
  const paymentMethod = params.paymentMethodType?.split(" ");
  const paymentMethodType = paymentMethod ? paymentMethod[0] : null;
  const channelCode = paymentMethod ? paymentMethod[1] : null;

  const startDate = moment(newDate.setMonth(newDate.getMonth() - 3)).format(
    "YYYY-MM-DD"
  );

  const endDate = moment(`${endYear}-${endMonth}-${endDay} 00:00:00`)
    .add(1, "days")
    .format("YYYY-MM-DD HH:mm:ssZZ");

  return {
    brandId: removeBase64Name(
      "Brand:",
      params.brand !== undefined ? params.brand : []
    ),
    canceledAt: getGteLteVariables({
      gte: params.orderCanceledFrom
        ? moment(`${params.orderCanceledFrom} 00:00:00`).format(
            "YYYY-MM-DD HH:mm:ssZZ"
          )
        : null,
      lte: params.orderCanceledTo
        ? moment(`${params.orderCanceledTo} 00:00:00`)
            .add(1, "days")
            .format("YYYY-MM-DD HH:mm:ssZZ")
        : null
    }),
    channelCode: maybe(() =>
      channelCode ? findValueInEnum(channelCode, PaymentChannelCodeEnum) : null
    ),
    completedAt: getGteLteVariables({
      gte: params.orderCompleteDateFrom
        ? moment(`${params.orderCompleteDateFrom} 00:00:00`).format(
            "YYYY-MM-DD HH:mm:ssZZ"
          )
        : null,
      lte: params.orderCompleteDateTo
        ? moment(`${params.orderCompleteDateTo} 00:00:00`)
            .add(1, "days")
            .format("YYYY-MM-DD HH:mm:ssZZ")
        : null
    }),
    mallId: removeBase64Name(
      "Mall:",
      params.mall !== undefined ? params.mall : []
    ),

    orderDate: getGteLteVariables({
      gte: params.orderDateFrom
        ? moment(`${params.orderDateFrom} 00:00:00`).format(
            "YYYY-MM-DD HH:mm:ssZZ"
          )
        : !params.orderDateFrom &&
          (params.orderCompleteDateFrom || params.orderCanceledFrom)
        ? null
        : moment(`${startDate} 00:00:00`).format("YYYY-MM-DD HH:mm:ssZZ"),

      lte: params.orderDateTo
        ? moment(`${params.orderDateTo} 00:00:00`)
            .add(1, "days")
            .format("YYYY-MM-DD HH:mm:ssZZ")
        : !params.orderDateTo &&
          (params.orderCompleteDateTo || params.orderCanceledTo)
        ? null
        : endDate
    }),
    orderStatus: maybe(() => params.orderStatus, null),
    paymentMethodType,
    search: params.query,
    shippingMethodName: maybe(() => params.deliveryType, null)
  };
}

export function getFilterQueryParam(
  filter: IFilterElement<OrderLinesFilterKeys>,
  params: OrderLinesFinanceListUrlFilters
): OrderLinesFinanceListUrlFilters {
  const { active, group, name, value } = filter;

  if (!!group) {
    const rest = params && params[group] ? params[group] : undefined;

    return {
      [group]: active
        ? {
            ...(rest === undefined ? {} : rest),
            [name]: value
          }
        : rest
    };
  }

  switch (name) {
    case OrderLinesFilterKeys.mall:
      return getMultipleValueQueryParam(
        filter,
        OrderLinesFinanceListUrlFiltersWithMultipleValues.mall
      );
    case OrderLinesFilterKeys.brand:
      return getMultipleValueQueryParam(
        filter,
        OrderLinesFinanceListUrlFiltersWithMultipleValues.brand
      );
    case OrderLinesFilterKeys.orderStatus:
      return getMultipleValueQueryParam(
        filter,
        OrderLinesFinanceListUrlFiltersWithMultipleValues.orderStatus
      );
    case OrderLinesFilterKeys.orderDate:
      return getMinMaxQueryParam(
        filter,
        OrderLinesFinanceListUrlFiltersEnum.orderDateFrom,
        OrderLinesFinanceListUrlFiltersEnum.orderDateTo
      );
    case OrderLinesFilterKeys.completedAt:
      return getMinMaxQueryParam(
        filter,
        OrderLinesFinanceListUrlFiltersEnum.orderCompleteDateFrom,
        OrderLinesFinanceListUrlFiltersEnum.orderCompleteDateTo
      );
    case OrderLinesFilterKeys.canceledAt:
      return getMinMaxQueryParam(
        filter,
        OrderLinesFinanceListUrlFiltersEnum.orderCanceledFrom,
        OrderLinesFinanceListUrlFiltersEnum.orderCanceledTo
      );
    case OrderLinesFilterKeys.paymentMethodType:
      return getSingleEnumValueQueryParam(
        filter,
        OrderLinesFinanceListUrlFiltersEnum.paymentMethodType,
        paymentEnum
      );
    case OrderLinesFilterKeys.shippingMethodName:
      return getMultipleValueQueryParam(
        filter,
        OrderLinesFinanceListUrlFiltersWithMultipleValues.deliveryType
      );
  }
}

export const {
  deleteFilterTab,
  getFilterTabs,
  saveFilterTab
} = createFilterTabUtils<OrderLinesFinanceListUrlFilters>(
  ORDER_LINE_FINANCE_FILTERS_KEY
);

export const { areFiltersApplied, getActiveFilters } = createFilterUtils<
  OrderLinesUrlQueryParams,
  OrderLinesFinanceListUrlFilters
>({
  ...OrderLinesFinanceListUrlFiltersEnum,
  ...OrderLinesFinanceListUrlFiltersWithMultipleValues
});
